
// vue
import { defineComponent, ref, onMounted } from 'vue';

// components
import ClereTitle from '@/components/clere-title.vue';

// services
import { useTileManagementService } from '@/api/service/tile-management.service';
import { useEnvironmentService } from '@/api/service/environment.service';
import { useDataMigrationService } from '@/api/service/data-migration-service';

// hooks
import { useToast } from 'vue-toastification';

// vue3-material
// import { VmContainer, VmCard, VmCardContent, VmButton } from 'vue3-material';

export default defineComponent({
    components: {
        ClereTitle,
    },
    setup() {
        const { downloadFile, uploadFile } = useTileManagementService();
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const { restartTileserver } = useDataMigrationService;
        const { getConfigFile } = useEnvironmentService();

        const isUploading = ref(false);
        const fileInput = ref<HTMLInputElement>();

        // refs
        const isLoading = ref(true);

        // hooks
        const toast = useToast();

        // methods
        const handleDownloadFile = async () => {
            const response = await downloadFile('config.json');
            const blob = new Blob([response.data]);

            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'config.json';
            link.click();
            link.remove();
            toast.success('Successfully downloaded config.json');
        };

        const handleUploadFile = async () => {
            // eslint-disable-next-line no-use-before-define
            fileInput.value?.click(); // eslint-disable-line
        };

        const onFileSelected = async (event: Event) => {
            const target = event.target as HTMLInputElement;
            if (!target.files?.length) return;

            isUploading.value = true;

            try {
                const files = Array.from(target.files);
                await uploadFile(files, '');
                toast.success('Successfully updated config.json');
            } catch (e) {
                // eslint-disable-next-line no-use-before-define
                console.error(e); // eslint-disable-line
            } finally {
                isUploading.value = false;
                target.value = '';
            }
        };

        const handleRestartTileserver = async () => {
            const isRestarted = await restartTileserver();
            if (isRestarted) {
                toast.success('Successfully restarted tileserver');
            } else {
                toast.error('Tileserver restart failed');
            }
        };

        const generateConfig = async () => {
            const res = await getConfigFile();
            const mosaicData = res.data;
            const configList: any = {};

            mosaicData.keys.forEach((keys: any) => {
                mosaicData.mosaic.forEach((mosaic: any) => {
                    if (keys === mosaic.key) {
                        configList[keys] = {
                            mbtiles: mosaic.mbtiles,
                            tilejson: mosaic.tilejson,
                        };
                    }
                });
            });

            const mosaicJson = { data: configList };

            const mosaicStr = JSON.stringify(mosaicJson, null, 4);

            const blob = new Blob([mosaicStr], { type: 'application/json' });
            const configFile = new File([blob], 'config.json', {
                type: 'application/json',
            });

            try {
                await uploadFile([configFile], '');
                toast.success(
                    'Successfully uploaded Config Data. Restart tileserver to take effect!',
                );
            } catch (e) {
                // eslint-disable-next-line no-use-before-define
                console.error('Error generating or uploading config:', e); // eslint-disable-line
                toast.error('Failed to upload Config Data');
            }
        };

        // lifecycle hooks
        onMounted(async () => {
            isLoading.value = false;
        });

        return {
            isLoading,
            fileInput,
            handleRestartTileserver,
            generateConfig,
            handleDownloadFile,
            handleUploadFile,
            onFileSelected,
        };
    },
});
